// src/pages/profile-settings/PatientSettingsRoutes.jsx
import IntegrationsInteroperability from '@/pages/profile-settings/IntegrationsInteroperability.jsx';
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import PatientSettingsHome from "./patient/SettingsHome.jsx";

import Appearance from "./patient/Appearance.jsx";
import AccessibilityFola from "./patient/AccessibilityFola.jsx";

// TopBar wrappers
import ProfilePage from "./screens/ProfilePage.jsx";
import LoginSecurityPage from "./screens/LoginSecurityPage.jsx";
import TwoFactorPage from "./screens/TwoFactorPage.jsx";

// Pages
import Notifications from "./patient/Notifications.jsx";

// Edit pages
import EditIntro from "./patient/EditIntro.jsx";
import Insurance from "@/pages/profile-settings/patient/Insurance.jsx";
import Dependants from "@/pages/profile-settings/patient/Dependants";
import EditName from "./patient/EditName.jsx";
import EditDob from "./patient/EditDob.jsx";
import EditPhone from "./patient/EditPhone.jsx";
import EditEmail from "./patient/EditEmail.jsx";
import EditIdPassport from "./patient/EditIdPassport.jsx";
import PatientEHR from "@/pages/profile-settings/patient/PatientEHR.jsx";
import EhrPasswordReset from "@/pages/profile-settings/patient/EhrPasswordReset.jsx";
import EhrPasswordResetConfirm from "@/pages/profile-settings/patient/EhrPasswordResetConfirm.jsx";
import EditAddress from "./patient/EditAddress.jsx";

// Additional profile detail screens
import Language from "./patient/Language.jsx";
import NotificationsNews from "@/pages/profile-settings/patient/NotificationsNews.jsx";
import BloodType from "./patient/BloodType.jsx";
import Allergies from "./patient/Allergies.jsx";
import Conditions from "./patient/Conditions.jsx";
import Medications from "./patient/Medications.jsx";
import OrganDonor from "./patient/OrganDonor.jsx";
import HeightWeight from "./patient/HeightWeight.jsx";

// Login & security detail screens
import ChangePasscode from "./patient/ChangePasscode.jsx";
import ChangePassword from "./patient/ChangePassword.jsx";
import TwoFactorSms from "./patient/TwoFactorSms.jsx";
import TwoFactorAuthApp from "./patient/TwoFactorAuthApp.jsx";

// Settings-level system language screen
import SettingsLanguage from "./patient/SettingsLanguage.jsx";
import Preferences from "./patient/Preferences.jsx";
import ReferralSharing from "./patient/ReferralSharing.jsx";
import AlertsAndSounds from "./patient/AlertsAndSounds.jsx";
import PlansSubscriptionsPage from "@/features/settings/preferences/PlansSubscriptionsPage.jsx";
import TransactionsInvoicesPage from "@/features/settings/preferences/TransactionsInvoicesPage.jsx";
import DownloadsPage from "@/features/settings/preferences/DownloadsPage.jsx";
import AccountLifecyclePage from "@/features/settings/preferences/AccountLifecyclePage.jsx";
import SponsorPaymentLinkPage from "@/features/settings/preferences/SponsorPaymentLinkPage.jsx";

// --- Local fallback: BareRoute (no-op wrapper)
function BareRoute({ children }) {
  return <>{children}</>;
}

// Keep historical patient bookmarks working while giving the plain-language
// Health records & sharing surface one canonical address.
function LegacyInteroperabilityRedirect() {
  const location = useLocation();
  return (
    <Navigate
      replace
      to={`/settings/patient/preferences/health-records-sharing${location.search}${location.hash}`}
    />
  );
}

/**
 * PatientSettingsRoutes
 *
 * All /settings routes when the current user acts as a patient.
 */
export default function PatientSettingsRoutes() {
  return (
    <Routes>
      {/* Home */}
      <Route index element={<PatientSettingsHome />} />

      {/* Profile list */}
      <Route path="p" element={<ProfilePage />} />

      {/* Login & Security */}
      <Route path="login-security" element={<LoginSecurityPage />} />

      <Route
        path="p/ehr/reset-password"
        element={
          <BareRoute>
            <EhrPasswordReset />
          </BareRoute>
        }
      />

      <Route
        path="p/ehr/reset-password/confirm"
        element={
          <BareRoute>
            <EhrPasswordResetConfirm />
          </BareRoute>
        }
      />

      <Route
        path="login-security/change-passcode"
        element={
          <BareRoute>
            <ChangePasscode />
          </BareRoute>
        }
      />
      <Route
        path="login-security/change-password"
        element={
          <BareRoute>
            <ChangePassword />
          </BareRoute>
        }
      />
      <Route
        path="login-security/create-password"
        element={
          <BareRoute>
            <ChangePassword intent="create" />
          </BareRoute>
        }
      />

      {/* Two-factor hub + flows (all nested under login-security) */}
      <Route path="login-security/two-factor" element={<TwoFactorPage />} />
      <Route
        path="login-security/two-factor/sms"
        element={
          <BareRoute>
            <TwoFactorSms />
          </BareRoute>
        }
      />
      <Route
        path="login-security/two-factor/auth-app"
        element={
          <BareRoute>
            <TwoFactorAuthApp />
          </BareRoute>
        }
      />

      {/* System Language (Settings > Language) */}
      <Route
        path="language"
        element={
          <BareRoute>
            <SettingsLanguage />
          </BareRoute>
        }
      />

      {/* Preferences (app-level settings) */}
      <Route
        path="preferences"
        element={
          <BareRoute>
            <Preferences />
          </BareRoute>
        }
      />
      <Route path="preferences/health-records-sharing" element={<IntegrationsInteroperability role="patient" />} />
      <Route path="preferences/integrations-interoperability" element={<LegacyInteroperabilityRedirect />} />
      <Route
        path="preferences/appearance"
        element={
          <BareRoute>
            <Appearance />
          </BareRoute>
        }
      />
      <Route
        path="preferences/accessibility-fola"
        element={
          <BareRoute>
            <AccessibilityFola />
          </BareRoute>
        }
      />
      <Route
        path="preferences/notifications"
        element={
          <BareRoute>
            <NotificationsNews />
          </BareRoute>
        }
      />
      <Route
        path="preferences/alerts-sounds"
        element={
          <BareRoute>
            <AlertsAndSounds />
          </BareRoute>
        }
      />
      <Route
        path="preferences/referral-sharing"
        element={
          <BareRoute>
            <ReferralSharing />
          </BareRoute>
        }
      />
      <Route
        path="preferences/notifications-news"
        element={<Navigate to="/settings/patient/preferences/notifications" replace />}
      />
      <Route
        path="preferences/plans"
        element={
          <BareRoute>
            <PlansSubscriptionsPage role="patient" />
          </BareRoute>
        }
      />
      <Route
        path="preferences/transactions"
        element={
          <BareRoute>
            <TransactionsInvoicesPage role="patient" />
          </BareRoute>
        }
      />
      <Route
        path="preferences/transactions/prepare-sponsor-payment"
        element={
          <BareRoute>
            <SponsorPaymentLinkPage />
          </BareRoute>
        }
      />
      <Route
        path="preferences/downloads"
        element={
          <BareRoute>
            <DownloadsPage role="patient" />
          </BareRoute>
        }
      />
      <Route
        path="preferences/account-lifecycle"
        element={
          <BareRoute>
            <AccountLifecyclePage role="patient" />
          </BareRoute>
        }
      />

      {/* Notifications */}
      <Route
        path="notifications"
        element={
          <BareRoute>
            <Notifications />
          </BareRoute>
        }
      />

      {/* PROFILE EDIT PAGES */}
      <Route
        path="p/edit-intro"
        element={
          <BareRoute>
            <EditIntro />
          </BareRoute>
        }
      />
      <Route
        path="insurance"
        element={
          <BareRoute>
            <Insurance />
          </BareRoute>
        }
      />
      <Route
        path="dependants"
        element={
          <BareRoute>
            <Dependants />
          </BareRoute>
        }
      />
      <Route
        path="p/edit-name"
        element={
          <BareRoute>
            <EditName />
          </BareRoute>
        }
      />
      <Route
        path="p/edit-dob"
        element={
          <BareRoute>
            <EditDob />
          </BareRoute>
        }
      />
      <Route
        path="p/edit-phone"
        element={
          <BareRoute>
            <EditPhone />
          </BareRoute>
        }
      />
      <Route
        path="p/edit-email"
        element={
          <BareRoute>
            <EditEmail />
          </BareRoute>
        }
      />
      <Route
        path="p/edit-id-passport"
        element={
          <BareRoute>
            <EditIdPassport />
          </BareRoute>
        }
      />
      <Route
        path="p/ehr"
        element={
          <BareRoute>
            <PatientEHR />
          </BareRoute>
        }
      />
      <Route
        path="p/edit-address"
        element={
          <BareRoute>
            <EditAddress />
          </BareRoute>
        }
      />

      {/* Detail screens */}
      <Route
        path="p/languages"
        element={
          <BareRoute>
            <Language />
          </BareRoute>
        }
      />
      <Route
        path="p/notifications-news"
        element={<Navigate to="/settings/patient/preferences/notifications-news" replace />}
      />
      <Route
        path="p/blood-type"
        element={
          <BareRoute>
            <BloodType />
          </BareRoute>
        }
      />
      <Route
        path="p/allergies"
        element={
          <BareRoute>
            <Allergies />
          </BareRoute>
        }
      />
      <Route
        path="p/conditions"
        element={
          <BareRoute>
            <Conditions />
          </BareRoute>
        }
      />
      <Route
        path="p/medications"
        element={
          <BareRoute>
            <Medications />
          </BareRoute>
        }
      />
      <Route
        path="p/organ-donor"
        element={
          <BareRoute>
            <OrganDonor />
          </BareRoute>
        }
      />
      <Route
        path="p/height-weight"
        element={
          <BareRoute>
            <HeightWeight />
          </BareRoute>
        }
      />
      
      {/* Fallback */}
      <Route path="*" element={<Navigate to="/settings/patient" replace />} />
    </Routes>
  );
}
